import * as core from '@angular/core';
import { EMPTY, fromEvent, Observable, zip } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { MatListItem } from '@angular/material/list';
import { CsLocationSetup, LegendItem } from '../service/cs-installation-section.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CsBuildupStateQuery, CsBuildupStateStore, EditableSectionPartComponent } from '../../shared';
import { ElementRef } from '@angular/core';

@core.Component({
  selector: 'app-location-setup',
  templateUrl: './location-setup.component.html',
  styleUrls: ['./location-setup.component.scss']
})
export class LocationSetupComponent extends EditableSectionPartComponent implements core.OnInit, core.AfterViewInit, core.OnDestroy {
  @core.ViewChild('locationSetupCanvas') public canvas: core.ElementRef;
  @core.ViewChild('backgroundFileInput') public backgroundFileInput: core.ElementRef;
  @core.ViewChild('legend', {read: core.ElementRef}) private legend: core.ElementRef;
  @core.ViewChildren(MatListItem, {read: core.ElementRef}) listItems: core.QueryList<core.ElementRef>;
  readonly defalutLineWidth = 4;
  readonly defaultStrokeStyle = 'cadetblue';

  @core.Input() csLocationSetup: CsLocationSetup;
  @core.Output() discardChangesCalled = new core.EventEmitter();
  @core.Output() saveChangesCalled = new core.EventEmitter<CsLocationSetup>();
  @core.Output() deleteCalled = new core.EventEmitter();

  scaleFactor = 1;
  strokeStyle = this.defaultStrokeStyle;

  public readonly maxBackgroundFileSizeInKb = 300;
  private readonly canvasWidth = 400;
  private readonly canvasHeight = 400;
  private previousDrawingIsBackground: boolean;
  private backgroundImage: HTMLImageElement;
  private fileSelectedEvent: any;
  private croppedBackgoundImage: any;
  private internalIsCropping: boolean;
  private ctx: CanvasRenderingContext2D;
  private startPos: {x: number, y: number};
  private sourceOffset: {x: number, y: number};
  private sourceSize: {width: number, height: number};

  private lines: {start: {x: number, y: number}, end: {x: number, y: number}, width: number, strokeStyle: string}[] = [];
  private htmlElements: {x: number, y: number, width: number, height: number, svg: string, img: HTMLImageElement}[] = [];

  get backgroundFileSelectedEvent(): any {
    return this.fileSelectedEvent;
  }

  get isCropping(): boolean {
    return this.internalIsCropping;
  }

  get isReadyForDrawing(): boolean {
    return !this.isReadonly && !this.isCropping && this.hasBackgroundImage;
  }

  get hasBackgroundImage(): boolean {
    return !!this.backgroundImage;
  }

  get hasDrawElements(): boolean {
    return this.lines.length > 0 || this.htmlElements.length > 0 || this.previousDrawingIsBackground;
  }

  private get lineWidth(): number {
    return this.defalutLineWidth * this.scaleFactor;
  }

  constructor(
    private readonly snackBar: MatSnackBar,
    protected element: ElementRef<Element>,
    protected csBuildupStore: CsBuildupStateStore,
    protected csBuildupStateQuery: CsBuildupStateQuery) {
    super(element, csBuildupStore, csBuildupStateQuery);
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (!this.csLocationSetup?.drawing) {
      super.startEdit();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public ngAfterViewInit(): void {
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;

    canvasEl.width = this.canvasWidth;
    canvasEl.height = this.canvasHeight;

    this.ctx = canvasEl.getContext('2d');
    this.ctx.lineCap = 'round';

    this.captureMouseEvents(canvasEl);
    this.initializeFromCsLocationSetup();
  }

  newBackgroundFileSelected(event: any): void {
    const value = event.target.value;
    if (!value) { return; }
    if (event.target.files[0].size  > 1024 * this.maxBackgroundFileSizeInKb) {
      this.snackBar.open(
        `Bitte wählen sie eine Datei aus, die eine Größe von ${this.maxBackgroundFileSizeInKb} KB nicht überschreitet.`,
        undefined, {
          politeness: 'assertive',
          duration: 5000,
          verticalPosition: 'top',
        });
      return;
    }
    this.fileSelectedEvent = event;
    this.startCropping();
  }

  private startCropping(): void {
    this.internalIsCropping = true;
  }

  endCropping(): void {
    this.backgroundFileInput.nativeElement.value = '';
    this.internalIsCropping = false;
  }

  backgroundImageCropped(event: ImageCroppedEvent): void {
    this.croppedBackgoundImage = event.base64;
  }

  setBackgroundImage(imageData?: string): void {
    if (!imageData) { this.previousDrawingIsBackground = false; }
    createAndRenderImage(imageData ?? this.croppedBackgoundImage, this.ctx, 0, 0, this.canvasWidth, this.canvasHeight)
      .subscribe(image => {
        this.backgroundImage = image;
        this.endCropping();
      });
  }

  clearDrawing(): void {
    this.clearCanvas();
    if (this.previousDrawingIsBackground) {
      this.setBackgroundImage(this.csLocationSetup.backgroundImage);
      this.previousDrawingIsBackground = false;
    }
    else {
      this.drawBackgroundImageOnCanvas();
    }
    this.clearDrawingElements();
  }

  clearDrawingElements(): void {
    this.lines = [];
    this.htmlElements = [];
  }

  discardChanges(): void {
    this.clearDrawingElements();
    this.initializeFromCsLocationSetup();
    this.discardChangesCalled.emit();
    this.endEdit();
  }

  saveChanges(): void {
    this.getLegendItems().subscribe(legendItems => {
      const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
      const updatedCsLocationSetup: CsLocationSetup = {
        id: this.csLocationSetup.id,
        backgroundImage: this.previousDrawingIsBackground ? this.csLocationSetup.backgroundImage : this.backgroundImage.src,
        drawing: canvasEl.toDataURL(),
        legendItems
      };
      this.saveChangesCalled.emit(updatedCsLocationSetup);
      this.endEdit();
    });
  }

  delete(): void{
    this.deleteCalled.emit();
  }

  onDragStart(e: DragEvent): void {
    if (!this.isReadyForDrawing) { return; }
    const htmlElement = e.target as HTMLElement;
    e.dataTransfer.setData('text/html', htmlElement.outerHTML);
    e.dataTransfer.effectAllowed = 'copyMove';

    const rect = htmlElement.getBoundingClientRect();
    this.sourceSize = { width: rect.width, height: rect.height };
    this.sourceOffset = { x: e.offsetX, y: e.offsetY };
  }

  onDragOver(e: DragEvent): boolean {
    if (!this.isReadyForDrawing) { return; }
    if (e.preventDefault) {
      e.preventDefault();
    }
    return false;
  }

  onDrop(e: DragEvent): void {
    if (!this.isReadyForDrawing) { return; }
    const html = e.dataTransfer.getData('text/html');
    const xml = htmlToXml(html);
    const svg = xmlToSvg(xml, this.sourceSize.width, this.sourceSize.height);
    const htmlElement = {
      x: e.offsetX - this.sourceOffset.x,
      y: e.offsetY - this.sourceOffset.y,
      width: this.sourceSize.width,
      height: this.sourceSize.height,
      svg,
      img: undefined
    };
    this.htmlElements.push(htmlElement);

    createAndRenderImage(htmlElement.svg, this.ctx, htmlElement.x, htmlElement.y, htmlElement.width, htmlElement.height)
      .subscribe(img => htmlElement.img = img);
  }

  private captureMouseEvents(canvasEl: HTMLCanvasElement): void {
    // this will capture all mousedown events from the canvas element
    fromEvent(canvasEl, 'mousedown')
      .pipe(
        switchMap((e: MouseEvent) => {
          if (!this.isReadyForDrawing) { return EMPTY; }
          this.startPos = {
            x: e.offsetX,
            y: e.offsetY
          };
          // after a mouse down, we'll record all mouse moves
          return fromEvent(canvasEl, 'mousemove')
            .pipe(
              // we'll stop (and unsubscribe) once the user releases the mouse
              // this will trigger a 'mouseup' event
              takeUntil(fromEvent(canvasEl, 'mouseup')),
              // we'll also stop (and unsubscribe) once the mouse leaves the canvas (mouseleave event)
              takeUntil(fromEvent(canvasEl, 'mouseleave'))
            );
        })
      )
      .subscribe((e: MouseEvent) => {
        const currentPos = { x: e.offsetX, y: e.offsetY };
        const currentLine = createLine(this.startPos, currentPos, this.lineWidth, this.strokeStyle);
        this.clearCanvas();
        this.drawBackgroundImageOnCanvas();
        this.lines.concat(currentLine).forEach(line => {
          this.ctx.strokeStyle = line.strokeStyle;
          this.ctx.lineWidth = line.width;
          drawLineOnCanvas(this.ctx, line.start, line.end);
        });
        this.htmlElements.forEach(element =>
          this.ctx.drawImage(element.img, element.x, element.y));
      });

    fromEvent(canvasEl, 'mouseup').subscribe((e: MouseEvent) => {
      if (!this.startPos) { return; }
      const newLine = createLine(
        this.startPos,
        {x: e.offsetX, y: e.offsetY},
        this.lineWidth,
        this.strokeStyle);
      this.lines.push(newLine);
      this.startPos = undefined;
    });
  }

  private initializeFromCsLocationSetup(): void {
    if (this.csLocationSetup?.drawing) {
      this.previousDrawingIsBackground = true;
      this.setBackgroundImage(this.csLocationSetup.drawing);
    }
  }


  private clearCanvas(): void {
    this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
  }

  private drawBackgroundImageOnCanvas(): void {
    if (!this.backgroundImage) { return; }
    this.ctx.drawImage(this.backgroundImage, 0, 0, this.canvasWidth, this.canvasHeight);
  }

  private getLegendItems(): Observable<LegendItem[]> {
    const observables: Observable<LegendItem>[] = [];
    let stackRank = 1;
    this.listItems.forEach(item => {
      const htmlElement = item.nativeElement as HTMLElement;
      const label = htmlElement.getElementsByClassName('legend-item-label')[0];
      const icon = htmlElement.getElementsByClassName('legend-item-icon')[0];
      observables.push(this.getLegendIconBase64Encoded(icon)
        .pipe(map(base64 => ({icon: base64, text: label.innerHTML, stackRank: stackRank++}))));
    });
    return zip(...observables);
  }

  private getLegendIconBase64Encoded(htmlElement: Element): Observable<string> {
    const rect = htmlElement.getBoundingClientRect();
    const xml = htmlToXml(htmlElement.outerHTML);
    const svg = xmlToSvg(xml, rect.width, rect.height);
    return getBase64Encoded(svg, rect.width, rect.height);
  }
}

function createLine(start: { x: number, y: number }, end: { x: number, y: number }, width: number, strokeStyle: string): any {
  return {
    start,
    end,
    width,
    strokeStyle
  };
}

function drawLineOnCanvas(ctx: CanvasRenderingContext2D, startPos: { x: number, y: number }, endPos: { x: number, y: number }): void {
  if (!ctx) { return; }

  ctx.beginPath();
  if (startPos) {
    ctx.moveTo(startPos.x, startPos.y);
    ctx.lineTo(endPos.x, endPos.y);
    ctx.stroke();
  }
}

function getBase64Encoded(imageSrc: string, width: number, height: number): Observable<string> {
  const img = new Image();
  const result$ = fromEvent(img, 'load')
    .pipe(map(() => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL();
    }));
  img.src = imageSrc;

  return result$;
}

function createAndRenderImage(imageData: string, ctx: CanvasRenderingContext2D,
                              x: number, y: number, width: number, height: number): Observable<HTMLImageElement> {
  const img = new Image();
  const result$ = fromEvent(img, 'load')
    .pipe(map(() => {
      ctx.drawImage(img, x, y, width, height);
      return img;
    }));
  img.src = imageData;
  return result$;
}

function xmlToSvg(xml: string, width: number, height: number): string {
  return 'data:image/svg+xml;charset=utf-8,' +
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
    '<foreignObject width="100%" height="100%">' +
    xml +
    '</foreignObject>' +
    '</svg>';
}

function htmlToXml(html: string): string {
  const doc = document.implementation.createHTMLDocument('');
  doc.write(html);

  // You must manually set the xmlns if you intend to immediately serialize
  // the HTML document to a string as opposed to appending it to a
  // <foreignObject> in the DOM
  doc.documentElement.setAttribute('xmlns', doc.documentElement.namespaceURI);

  // Do not forget to remove the margin from the body element to avoid unexpected behavior
  doc.body.style.margin = '0';

  // Get well-formed markup
  let xml = (new XMLSerializer()).serializeToString(doc.body);
  xml = xml.replace(/\#/g, '%23');

  return xml;
}
